import { inject } from '@angular/core';
import { Route, UrlSegment, Router, UrlTree } from '@angular/router';
import { RootScopeService } from '../services/root-scope.service';

type FeatureFlagMatchFn = (route: Route, segments: UrlSegment[]) => boolean | UrlTree | Promise<boolean | UrlTree>;

export const featureFlagCanMatch: FeatureFlagMatchFn = (
  route: Route,
  _segments: UrlSegment[]
): boolean | UrlTree | Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const root = inject(RootScopeService);

  const flagKey = route.data?.['flag'] as string | undefined;
  if (!flagKey) return true; // No flag requirement

  const ready = root.launchDarklyReady?.() ?? false;

  // If LaunchDarkly is ready, check immediately
  if (ready) {
    const enabled = isFlagEnabled(flagKey, root)?.valueOf();
    if (enabled === true) {
      return true;
    } else {
      console.log(` Access denied for ${flagKey} - redirecting to entries`);
      return router.parseUrl('/entries');
    }
  }

  // If not ready, wait for it with timeout
  return new Promise<boolean | UrlTree>((resolve) => {
    const maxWait = 10000; // 10 seconds
    const startTime = Date.now();

    const checkReady = () => {
      const isReady = root.launchDarklyReady?.() ?? false;

      if (isReady) {
        const enabled = isFlagEnabled(flagKey, root);
        if (enabled === true) {
          console.log(` Access granted for ${flagKey}`);
          resolve(true);
        } else {
          console.log(` Access denied for ${flagKey} - redirecting to entries`);
          resolve(router.parseUrl('/entries'));
        }
      } else {
        const elapsed = Date.now() - startTime;
        if (elapsed >= maxWait) {
          console.warn(` LaunchDarkly timeout for ${flagKey} - denying access for security`);
          resolve(router.parseUrl('/entries'));
        } else {
          setTimeout(checkReady, 200); // Check every 200ms
        }
      }
    };

    checkReady();
  });
};

function isFlagEnabled(flagKey: string, root: RootScopeService): boolean {
  // Return raw signal value (can be true | false | undefined) so guard can distinguish 'not yet loaded'.
  switch (flagKey) {
    case 'failed-uploads-flag': return root.failedUploadsFlag().getValue() ?? false;
    case 'db-admin-flag': return root.dbAdminFlag().getValue() ?? false;
    // case 'failed-uploads-flag': return false;
    default: return false; // unknown flag → treat as disabled
  }
}